import React, { useCallback, useMemo } from 'react';
import { useField } from 'formik';
import clsx from 'clsx';
import { IFormItem } from '../form-item.type';
import { FormErrorMsg } from '../form-error-msg';
import { MAX_NUMBER_FORMAT, REGEX_PATTERN } from 'consts/common.const';

interface IInput extends IFormItem {
  /**
   * Default: 'text'
   */
  type?: 'text' | 'number' | 'email' | 'password';

  placeholder?: string;

  /**
   * Min value for input number
   */
  min?: number;

  /**
   * Max value for input number
   */
  max?: number;

  className?: string;

  id?: string;

  isAllowOnlyNumber?: boolean;
  showError?: boolean;
  step?: string;
  isFormatNumber?: boolean;
}

export function Input({
  name,
  disabled = false,
  type = 'text',
  placeholder = '',
  min = undefined,
  max = undefined,
  className = '',
  id,
  onChange,
  isAllowOnlyNumber = false,
  showError = true,
  step,
  isFormatNumber = false,
}: IInput) {
  const [field, , helper] = useField(name);
  const inputType = useMemo(() => type || 'text', [type]);

  const handleChange = useCallback(
    (e) => {
      if (isAllowOnlyNumber && e.target.value && !REGEX_PATTERN.number.test(e.target.value)) {
        return;
      }

      field.onChange(e);
      onChange && onChange(e);
    },
    [field, isAllowOnlyNumber, onChange]
  );

  const handleKeyPress = useCallback(
    (e) => {
      if (!isFormatNumber) return;

      const keyCode = e.which ? e.which : e.keyCode;

      if (keyCode < 48 || keyCode > 57 || +e.target.value.replaceAll(',', '') > MAX_NUMBER_FORMAT) {
        e.preventDefault();
      }
    },
    [isFormatNumber]
  );

  const handleKeyUp = useCallback(
    (e) => {
      if (!isFormatNumber) return;

      const regNumberComma = /^[0-9,]+$/;

      if (!regNumberComma.test((e.target as HTMLInputElement).value.trim())) {
        helper.setValue('');

        return;
      }

      const value = (e.target as HTMLInputElement).value.replaceAll(',', '');

      helper.setValue(value ? (+value).toLocaleString('en') : '');
    },
    [helper, isFormatNumber]
  );

  return (
    <>
      <input
        name={name}
        disabled={disabled}
        type={inputType}
        min={min}
        max={max}
        placeholder={placeholder}
        className={clsx('form-control', className)}
        onChange={handleChange}
        onBlur={field.onBlur}
        onKeyUp={handleKeyUp}
        onKeyPress={handleKeyPress}
        value={field.value}
        id={id}
        step={step}
      />

      {showError && <FormErrorMsg name={name} />}
    </>
  );
}
